import { useCallback } from "react";
import CsvImporter from "@/components/CsvImporter";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listSitesLite, listBaysLite } from "@/lib/api";
import { supabase } from "@/lib/supabaseClient";

/**
 * /admin/import/bookings — bulk-create bookings via CSV.
 *
 * Direct supabase insert into lbs_bookings — bypasses the form helper
 * since we already provide flat ISO timestamps. Triggers fill
 * client_id / owner_user_id / created_by_user_id / reference_number.
 */

const COLUMNS = [
  { key: "site_code",        label: "Site code",        required: true,
    description: "lbs_sites.code within the active tenant.", example: "MEL01" },
  { key: "bay_code",         label: "Bay code",
    description: "Optional. lbs_bays.code within the chosen site.", example: "B1" },
  { key: "direction",        label: "Direction",        required: true,
    description: "inbound or outbound.", example: "inbound" },
  { key: "scheduled_start",  label: "Start (ISO 8601)", required: true,
    description: "ISO timestamp with timezone, e.g. 2026-03-01T08:00:00+11:00.",
    example: "2026-03-01T08:00:00+11:00" },
  { key: "scheduled_end",    label: "End (ISO 8601)",   required: true,
    description: "ISO timestamp with timezone.",
    example: "2026-03-01T09:00:00+11:00" },
  { key: "sender_company",   label: "Sender company",
    description: "Free text.", example: "Acme Foods Pty Ltd" },
  { key: "receiver_company", label: "Receiver company",
    description: "Free text.", example: "My Company" },
  { key: "vehicle_type",     label: "Vehicle type",
    description: "Picklist value (vehicle_type).", example: "B-double" },
  { key: "pallet_type",      label: "Pallet type",
    description: "Picklist value (pallet_type).", example: "Chep" },
  { key: "pallets_in",       label: "Pallets in",
    description: "Integer.", example: "26" },
  { key: "pallets_out",      label: "Pallets out",
    description: "Integer.", example: "0" },
  { key: "po_number",        label: "PO number",
    description: "Free text.", example: "PO-12345" },
  { key: "external_ref",     label: "External reference",
    description: "Customer-side reference.", example: "CUST-789" },
  { key: "notes",            label: "Notes",
    description: "Free text.", example: "Refrigerated, -18C." },
];

const DIRECTIONS = new Set(["inbound", "outbound"]);

export default function AdminImportBookings() {
  const { assignments, hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();

  const targetClientId =
    isSuperAdmin ? (viewAsClientId || null)
                 : (assignments?.[0]?.client_id || null);

  const loadContext = useCallback(async () => {
    if (!targetClientId) {
      throw new Error(isSuperAdmin
        ? "Super admin: please pick a client in the top bar first."
        : "Your account has no tenant assignment.");
    }
    const { data: sites } = await listSitesLite(targetClientId);
    const sitesByCode = new Map();
    (sites || []).forEach((s) => { if (s.code) sitesByCode.set(s.code.toLowerCase(), s); });

    // Pre-load bays for every site so bay_code resolves without N+1 lookups.
    const baysBySiteAndCode = new Map();
    await Promise.all((sites || []).map(async (s) => {
      const { data: bays } = await listBaysLite(s.id);
      (bays || []).forEach((b) => {
        if (b.code) baysBySiteAndCode.set(`${s.id}::${b.code.toLowerCase()}`, b);
      });
    }));

    return { clientId: targetClientId, sitesByCode, baysBySiteAndCode };
  }, [targetClientId, isSuperAdmin]);

  const validateRow = (raw, ctx) => {
    const errors = [];
    let level = "valid";

    const siteCode = (raw.site_code || "").trim();
    const site = siteCode ? ctx.sitesByCode.get(siteCode.toLowerCase()) : null;
    if (!siteCode) errors.push("site_code required");
    else if (!site) errors.push(`site "${siteCode}" not found`);

    let bayId = null;
    const bayCode = (raw.bay_code || "").trim();
    if (bayCode && site) {
      const b = ctx.baysBySiteAndCode.get(`${site.id}::${bayCode.toLowerCase()}`);
      if (!b) { errors.push(`bay "${bayCode}" not found at site`); level = "warn"; }
      else bayId = b.id;
    }

    const direction = (raw.direction || "").trim().toLowerCase();
    if (!direction) errors.push("direction required");
    else if (!DIRECTIONS.has(direction)) errors.push("direction must be inbound or outbound");

    const start = (raw.scheduled_start || "").trim();
    const end   = (raw.scheduled_end   || "").trim();
    const startDate = start ? new Date(start) : null;
    const endDate   = end   ? new Date(end)   : null;
    if (!start || isNaN(startDate?.getTime())) errors.push("scheduled_start invalid");
    if (!end   || isNaN(endDate?.getTime()))   errors.push("scheduled_end invalid");
    if (startDate && endDate && endDate <= startDate) errors.push("end must be after start");

    const intOrNull = (s) => {
      if (s === "" || s === null || s === undefined) return null;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? null : n;
    };

    // Errors that block import (anything not a "warn").
    if (errors.length > 0 && level !== "warn") level = "error";

    return {
      level,
      errors,
      normalised: {
        client_id: ctx.clientId,
        site_id: site?.id || null,
        bay_id: bayId,
        direction,
        scheduled_start: startDate?.toISOString() || null,
        scheduled_end:   endDate?.toISOString()   || null,
        sender_company:   (raw.sender_company   || "").trim() || null,
        receiver_company: (raw.receiver_company || "").trim() || null,
        vehicle_type:     (raw.vehicle_type     || "").trim() || null,
        pallet_type:      (raw.pallet_type      || "").trim() || null,
        pallets_in:  intOrNull(raw.pallets_in),
        pallets_out: intOrNull(raw.pallets_out),
        po_number:    (raw.po_number    || "").trim() || null,
        external_ref: (raw.external_ref || "").trim() || null,
        notes:        (raw.notes        || "").trim() || null,
        status: "draft",
      },
    };
  };

  const commitRow = async (n) => {
    const { error } = await supabase
      .from("lbs_bookings")
      .insert(n)
      .select("id")
      .maybeSingle();
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  };

  if (!hasRole("super_admin") && !hasRole("client_admin")) {
    return <p className="text-sm text-rose-700">Admins only.</p>;
  }

  return (
    <div data-testid="admin-import-bookings-page">
      {!targetClientId && (
        <div className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3" data-testid="bookings-import-no-tenant">
          {isSuperAdmin
            ? "Pick a client from the top-bar 'View as client' picker before importing."
            : "No tenant assignment found on your account."}
        </div>
      )}
      <CsvImporter
        title="Import bookings"
        testid="bookings-import"
        columns={COLUMNS}
        loadContext={loadContext}
        validateRow={validateRow}
        commitRow={commitRow}
      />
      <p className="text-xs text-slate-500 mt-4">
        Imported bookings land in <code>draft</code> status. Approval workflow still applies.
      </p>
    </div>
  );
}
